import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Fade,
  Grow,
  Paper,
  Divider,
  Stack,
  Alert,
  Skeleton,
  alpha,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import SourceIcon from '@mui/icons-material/Source';
import LinkIcon from '@mui/icons-material/Link';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../api/axiosConfig';
import parse from 'html-react-parser';
import ReactPlayer from 'react-player';
import { Tweet } from 'react-twitter-widgets';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
  '&:last-child': {
    marginBottom: 0
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.grey[50]
    : alpha(theme.palette.background.paper, 0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  '& p': {
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.7,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(0.5),
  verticalAlign: 'middle',
  color: theme.palette.text.secondary,
}));

const NewsChip = styled(Chip)(({ theme }) => ({
  height: '24px',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: '12px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  '& .MuiChip-icon': {
    fontSize: '16px',
  },
}));

const TitleChip = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(1),
  display: 'inline-block',
  marginTop: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

// Helper functions
const getSourceAvatarUrl = (source) => {
  if (!source) return '/assets/default.png';
  return `/assets/${source}.png`;
};

function parseHtmlContent(htmlString) {
  if (!htmlString) return null;

  const getTweetIdFromUrl = (url) => {
    const match = url.match(/status\/(\d+)/);
    return match ? match[1] : null;
  };

  return parse(htmlString, {
    replace: (domNode) => {
      if (domNode.name === 'img') {
        return null;
      }

      if (domNode.name === 'iframe' && domNode.attribs) {
        const src = domNode.attribs.src || '';
        if (src.includes('youtube.com') || src.includes('youtu.be')) {
          return (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '800px',
                  paddingTop: '56.25%',
                }}
              >
                <ReactPlayer
                  url={src}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </Box>
            </Box>
          );
        }
        return null;
      }

      if (domNode.name === 'a' && domNode.attribs?.href) {
        const href = domNode.attribs.href;
        if (href.includes('youtube.com') || href.includes('youtu.be')) {
          return (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '800px',
                  paddingTop: '56.25%',
                }}
              >
                <ReactPlayer
                  url={href}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </Box>
            </Box>
          );
        }
        if (href.includes('twitter.com') && href.includes('/status/')) {
          const tweetId = getTweetIdFromUrl(href);
          if (tweetId) {
            return (
              <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                <Tweet tweetId={tweetId} options={{ width: '100%' }} />
              </Box>
            );
          }
        }
      }

      return undefined;
    },
  });
}

// Loading skeleton component
const NewsSkeleton = () => (
  <Card sx={{ mb: 2, borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <Skeleton variant="rounded" width="70%" height={24} />
    </CardContent>
  </Card>
);

const NewsTab = ({ playerId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [newsList, setNewsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [openRows, setOpenRows] = useState({});

  // Edit state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [editedSource, setEditedSource] = useState('');
  const [editedUrl, setEditedUrl] = useState('');

  // Delete state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState(null);

  // Fetch news
  useEffect(() => {
    const fetchNews = async () => {
      if (!playerId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await api.get('/api/news', {
          params: { playerId },
        });
        setNewsList(response.data || []);

        // Auto-open first news
        if (response.data && response.data.length > 0) {
          setOpenRows({ [response.data[0].id]: true });
        }
      } catch (err) {
        setError(t('news.fetchError'));
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, [playerId]);

  // Handlers
  const handleToggleRow = useCallback((newsId) => {
    setOpenRows((prev) => ({
      ...prev,
      [newsId]: !prev[newsId],
    }));
  }, []);

  const handleEditClick = useCallback((newsItem) => {
    setCurrentNews(newsItem);
    setEditedTitle(newsItem.title || '');
    setEditedContent(newsItem.content || '');
    setEditedSource(newsItem.source || '');
    setEditedUrl(newsItem.url || '');
    setOpenEditDialog(true);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!currentNews) return;

    setSaving(true);
    try {
      const transformedContent = editedContent
        .replace(/<p><br><\/p>/g, '<br>')
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '<br>')
        .replace(/<br><br>/g, '<br>');

      const updateData = {
        title: editedTitle,
        content: transformedContent,
        source: editedSource,
        url: editedUrl,
      };

      await api.put(`/api/news/${currentNews.id}`, updateData);

      setNewsList((prev) =>
        prev.map((item) =>
          item.id === currentNews.id
            ? { ...item, ...updateData, content: transformedContent }
            : item
        )
      );

      setOpenEditDialog(false);
    } catch (error) {
      console.error('Error updating news:', error);
      setError(t('news.updateError'));
    } finally {
      setSaving(false);
    }
  }, [currentNews, editedTitle, editedContent, editedSource, editedUrl]);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setCurrentNews(null);
  }, []);

  const handleDeleteClick = useCallback((newsItem) => {
    setNewsToDelete(newsItem);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!newsToDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/api/news/${newsToDelete.id}`);
      setNewsList((prev) => prev.filter((n) => n.id !== newsToDelete.id));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting news:', error);
      setError(t('news.deleteError'));
    } finally {
      setDeleting(false);
    }
  }, [newsToDelete]);

  const cancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setNewsToDelete(null);
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <NewsSkeleton />
        <NewsSkeleton />
        <NewsSkeleton />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      </Box>
    );
  }

  // Render empty state
  if (newsList.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('news.noNewsAvailable')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('news.noNewsHint')}
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {newsList.map((newsItem, index) => (
          <Grow in timeout={300} key={newsItem.id} style={{ transitionDelay: `${index * 50}ms` }}>
            <StyledCard>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <HeaderBox onClick={() => handleToggleRow(newsItem.id)}>
                  {/* Left section with avatar and info */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      flexGrow: 1,
                      minWidth: 0,
                      gap: 2,
                    }}
                  >
                    <Tooltip title={newsItem.source || t('news.source')}>
                      <Avatar
                        src={getSourceAvatarUrl(newsItem.source)}
                        alt={newsItem.source}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `2px solid ${theme.palette.divider}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      />
                    </Tooltip>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      {/* Date, source and article link */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <IconWrapper>
                            <CalendarTodayIcon sx={{ fontSize: 14 }} />
                          </IconWrapper>
                          {new Date(newsItem.updated_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </Typography>

                        {newsItem.source && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <IconWrapper>
                              <SourceIcon sx={{ fontSize: 14 }} />
                            </IconWrapper>
                            {newsItem.source}
                          </Typography>
                        )}

                        {newsItem.url && (
                          <Chip
                            icon={<LinkIcon sx={{ fontSize: 14 }} />}
                            label={t('news.viewArticle')}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.info.main, 0.08),
                                borderColor: theme.palette.info.main,
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(newsItem.url, '_blank');
                            }}
                          />
                        )}
                      </Box>

                      {/* Title */}
                      <TitleChip>
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <NewspaperIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          {newsItem.title}
                        </Typography>
                      </TitleChip>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: { xs: 2, sm: 0 } }}>
                    <Tooltip title={t('news.editNews')}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(newsItem);
                        }}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('news.deleteNews')}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(newsItem);
                        }}
                        size="small"
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

                    <Tooltip title={openRows[newsItem.id] ? t('news.collapse') : t('news.expand')}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleRow(newsItem.id);
                        }}
                        size="small"
                        sx={{
                          transform: openRows[newsItem.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </HeaderBox>

                {/* Collapsible content */}
                <Collapse in={openRows[newsItem.id]} timeout="auto" unmountOnExit>
                  <ContentBox>
                    <Box sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {parseHtmlContent(newsItem.content)}
                    </Box>
                  </ContentBox>
                </Collapse>
              </CardContent>
            </StyledCard>
          </Grow>
        ))}

        {/* Edit Dialog */}
        <StyledDialog
          open={openEditDialog}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleCloseEditDialog();
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NewspaperIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('news.editNews')}</Typography>
            </Box>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 3 }}>
            {currentNews && (
              <Stack spacing={3}>
                <TextField
                  label={t('news.title')}
                  variant="outlined"
                  fullWidth
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <IconWrapper sx={{ ml: -0.5 }}>
                        <NewspaperIcon sx={{ fontSize: 20 }} />
                      </IconWrapper>
                    ),
                  }}
                />

                <TextField
                  label={t('news.source')}
                  variant="outlined"
                  fullWidth
                  value={editedSource}
                  onChange={(e) => setEditedSource(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <IconWrapper sx={{ ml: -0.5 }}>
                        <SourceIcon sx={{ fontSize: 20 }} />
                      </IconWrapper>
                    ),
                  }}
                />

                <TextField
                  label="URL"
                  variant="outlined"
                  fullWidth
                  value={editedUrl}
                  onChange={(e) => setEditedUrl(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <IconWrapper sx={{ ml: -0.5 }}>
                        <LinkIcon sx={{ fontSize: 20 }} />
                      </IconWrapper>
                    ),
                  }}
                />

                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    {t('news.content')}
                  </Typography>
                  <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                    <ReactQuill
                      theme="snow"
                      value={editedContent}
                      onChange={setEditedContent}
                      placeholder={t('news.editContentPlaceholder')}
                      style={{ minHeight: '200px' }}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['blockquote', 'code-block'],
                          ['link', 'image', 'video'],
                          ['clean']
                        ],
                      }}
                    />
                  </Paper>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={handleCloseEditDialog}
              color="inherit"
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleEditSave}
              variant="contained"
              disabled={saving}
              startIcon={saving && <CircularProgress size={16} />}
            >
              {saving ? t('news.saving') : t('common.save')}
            </Button>
          </DialogActions>
        </StyledDialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={cancelDelete}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
              <Typography variant="h6">{t('news.deleteNews')}</Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
              {t('news.confirmDeleteMessage')}
            </Alert>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={cancelDelete}
              color="inherit"
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting && <CircularProgress size={16} color="inherit" />}
            >
              {deleting ? t('news.deleting') : t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default NewsTab;